// Crayon threshold map for `pen='crayon'` — fractal wax-grain ordered dither
// (phase11-light-pens-2026-08-21.md §2 / §2.5 / §2.5.1 / §2.5.2).
//
// ## §2.5.2 (2026-08-21, 4th real-device pass) — world-rotated mapping
//
// Real Procreate crayon reference art showed the texture reads as
// **parallel streaks along the direction of travel**, not isotropic grain —
// no amount of octave-stacking on a canvas-locked `world` map can produce
// that, because a world map's texel content is independent of stroke
// direction by construction. The 3rd pass tried `mapping: 'stamp'` (the
// mechanism `bristle`/毛先 already uses) and got HALF of it right — the grid
// rotates with the stroke, so the elongated cells below did read as
// direction-following streaks — but `stamp` mapping ALSO translates the
// texel grid to the stamp's OWN centre on every dab. A sweep's ~`penWidth`
// overlapping dabs (1px-Chebyshev-step apart) therefore each look up a
// DIFFERENT texel for the SAME canvas pixel; since buffer accumulation is
// monotonic ('add'), "any ONE of ~penWidth independent per-dab samples inks"
// saturates almost the entire interior to solid fill (`sampleTexel`'s stamp
// branch — the stamp-mapping analogue of `airbrush-design §2.1`'s dab-
// averaging, but for COVERAGE instead of alpha). The only way the 3rd pass
// found to avoid that was to inflate `CRAYON_STREAK_STRETCH` to 32×, which
// hid the collapse but also erased all along-length variation — the result
// read as an unbroken "comb" (櫛目) the full length of a stroke, not the
// broken-up streaks the reference photos show.
//
// **The fix: rotate WITHOUT translating.** `mapping: 'world-rotated'`
// (`textureSampler.ts`) rotates the SAME way `stamp` does (by the
// precomputed `textureDir` = stroke direction) but samples raw canvas pixel
// coordinates, not stamp-centre-relative ones. A canvas pixel therefore maps
// to the SAME texel no matter which dab of the current segment's sweep hits
// it (no more per-dab translation ⇒ no more "any one hit ⇒ solid fill")
// while still following the stroke direction (rotation is a function of the
// SEGMENT, not the dab). Because a curving stroke's segments each carry a
// slightly different angle, a texel run that reads as one continuous streak
// under one segment's rotation naturally breaks up as later segments rotate
// the grid — which is exactly the "streaks that stop and start" look the
// reference photos have. This lets `CRAYON_STREAK_STRETCH` drop back down to
// a small single-digit multiplier (see that constant's doc) instead of the
// 32× the (now-abandoned) `stamp` attempt needed.
//
// `buildCrayonThresholdMap`'s cells are still **anisotropic** (X-stretched):
// every octave's cell is `CRAYON_STREAK_STRETCH`× wider along the map's X
// axis than its Y axis. Under `world-rotated`, X = `lx` (the rotated
// coordinate ALONG the stroke direction) and Y = `ly` (ACROSS it) — so a
// texel's ink/no-ink decision stays constant over a longer run as you sweep
// along the stroke than across it, which is what a "streak" is.
//
// This makes "the valley stays white forever" (the OLD §2.1 core property)
// no longer strictly true — a world-fixed map guaranteed a canvas TEXEL
// always saw the same map texel regardless of stroke angle; a rotated map's
// texel content depends on the sampling stroke's direction, so a
// different-angle overlapping stroke CAN eventually ink a spot a first
// stroke left white. This is not a regression: real crayon does the same (a
// second pass at a different angle fills gaps the first pass left). §2.5.2
// of the design doc re-derives the crayon/pastel distinction on this basis:
// crayon = DIRECTIONAL streaks (`world-rotated`), pastel = ISOTROPIC powder
// (`world`, unchanged) — the mechanism split (threshold-dither vs
// alpha-multiply, §2.1/§2.2) is untouched, only which AXIS carries the
// visual identity changed.
//
// ## Why a threshold map (reuses the `tone` seam, §2.1/§2.2)
//
// Crayon rides the SAME threshold-dither seam `pen='tone'` established
// (`textureThreshold: true` in `StampParams`) — a texel inks iff
// `threshold(worldX, worldY) < densityLevel`. §2.2 explains why a threshold
// (not alpha-multiply) is the right seam for crayon: a threshold is a binary
// per-texel decision, so the coarse on/off pattern reads as "wax grabbed the
// paper here, missed there" rather than the smooth mid-alpha haze
// alpha-multiply would produce.
//
// ## Why thresholds are never 0 (1..255)
//
// Mirrors `toneThreshold.ts`: `densityLevel` is `round(x*256)` ∈ [0, 256]. A
// threshold of 0 would ink unconditionally at `densityLevel=0` (nothing
// should ink at the floor), so every texel is clamped into `[1, 255]`.
//
// ## Replay determinism (was "the valley never fills in", §2.1 — narrowed by §2.5.2)
//
// The map is a STATIC array — the SAME (map-local) texel always returns the
// SAME threshold. Combined with the kernel being a pure function of
// `(canvas pixel, stroke direction)` under `world-rotated`, re-running the
// IDENTICAL stroke (same points, same width, same angle — e.g.
// `crayonStamp.test.ts`'s repeated-pass tests) samples the exact same texels
// every time, so a texel transparent after pass 1 is STILL transparent after
// pass N. This is what determinism buys for FREE, no matter the mapping
// mode. Under `mapping: 'world'` (pre-§2.5.2) this extended further: ANY
// stroke anywhere, at any angle, shared the same canvas-fixed texel grid, so
// a canvas-position valley was permanently white regardless of stroke angle.
// Under `mapping: 'world-rotated'` (current) the texel grid ROTATES with
// each stroke's own direction, so a SECOND stroke crossing the same canvas
// position at a DIFFERENT angle samples different map texels and CAN ink a
// spot the first stroke left white — matching how real crayon behaves. The
// core promise is "identical stroke ⇒ identical result" (replay/parity
// determinism), not "canvas position ⇒ permanent verdict".
//
// The §2.5.1 soft-edge kernel (see `rasterizer.ts`'s
// `CRAYON_THRESHOLD_SOFT_EDGE_WIDTH`) doesn't change any of this: texels
// whose threshold is `>= densityLevel` still never ink at all — only texels
// that already passed the binary test get a partial (rather than full)
// per-stamp alpha near the cutoff.
//
// ## Grain knob — "which octave dominates", not "the only scale" (§2.5.1)
//
// `penParams.crayon.grain` (0..2) picks the CELL SIZE of the DOMINANT
// (highest-amplitude) octave in a small fractal noise stack (below), one of
// 3 pre-baked maps. This is a PRESET SELECTION (like `texturePresetIndex`),
// not a runtime parameter fed into the kernel — so the per-pixel kernel
// gains zero new branches from `grain` itself, matching phase11-light-pens-
// 2026-08-21.md §1's "no new kernel" promise for the MAP (the separate
// soft-edge alpha kernel in `rasterizer.ts` is the one narrowly-scoped
// exception §2.5.1 explicitly calls out — see that file).
//
// ## History — three earlier designs, then rotate-without-translate (§2.5 / §2.5.1 / §2.5.2)
//
// **v1 (single low-freq-dominant scale)**: assigned the FULL 1..255
// amplitude to a per-BLOCK base value (block = 16/32/64px) and only a ±24
// jitter to the per-texel component. Since the threshold test is a binary
// comparison (`threshold < densityLevel`), a low-frequency-DOMINANT map
// means an entire block falls on one side of the threshold together — the
// block reads as a solid black or white square, and only blocks whose base
// landed within ±24 of the current `densityLevel` show any texel-level
// dithering. Result: a 16..64px checkerboard, not wax grain.
//
// **v2 (two-term high+low mix)**: fixed the block-collapse bug by making a
// SINGLE grain-cell hash (1..4px) the dominant high-frequency term, with a
// weak per-32px-block hash layered on top. This produced correct on/off
// texel-level dithering (no more block collapse) but still read as "荒い、
// クレヨンっぽくない" in a real device pass — a single independent-per-cell
// hash makes AXIS-ALIGNED SQUARE grains (every cell is an independent coin
// flip, so neighbouring cells have no spatial correlation) rather than the
// organic, nested-scale texture of a real wax/paper grain.
//
// **v3 (fractal octave stack, isotropic `world` mapping)**: layered MULTIPLE
// grain-cell scales — fixed the "grain looks like noise, not streaks"
// problem (real crayon reference photos, §2.5.2) is architecturally
// impossible for an isotropic world map to solve — no amount of octave
// stacking makes a canvas-locked map direction-dependent.
//
// **v4 (`mapping: 'stamp'`, anisotropic cells, §2.5.2 3rd pass)**: rotated
// AND translated to the stamp centre — got direction-following streaks but
// the per-dab translation collapsed coverage to solid fill (module doc
// above), needing a 32× stretch to hide it, which erased along-length
// variation (unbroken "comb", not the reference's broken streaks).
//
// **v5 (this file, `mapping: 'world-rotated'`)**: rotates WITHOUT
// translating (module doc above) — keeps the streaks, removes the collapse,
// lets the stretch shrink back to a small multiplier, and the SEGMENT-level
// (not dab-level) rotation naturally breaks streaks up along a curving
// stroke's length.
//
// The finest octave still contributes large ABSOLUTE per-texel variance
// (±110) everywhere, so individual texels inside any coarse patch keep
// enough independent swing to ink/skip differently from their neighbours —
// but the coarser octaves nudge whole neighbourhoods of finest-octave cells
// up/down TOGETHER, so the inked texels cluster into organic blobs instead
// of an independent-per-cell salt-and-pepper pattern. This is the standard
// "fractal/1-over-f-ish noise" construction, just with the amplitude ratio
// inverted from graphics' usual convention (there, LOW frequency dominates
// for terrain-like broad shapes; here the finest octave must keep large
// absolute variance — §2.5's lesson — because the threshold test is a
// binary per-texel decision, not an average).
//
// ## Determinism (ABSOLUTE — texture-brush-design.md §3, inherited here)
//
// Fixed integer formula only (`Math.imul` / shift / xor / `%`), no
// `Math.random`, no float transcendentals. 256×256; EVERY octave's cell size
// divides `CRAYON_THRESHOLD_SIZE` (256 = 2^8) — doubling a divisor of a
// power of two stays a divisor — so every grain level tiles seamlessly at
// every octave simultaneously.
export const CRAYON_THRESHOLD_SIZE = 256;
/**
 * Dominant-octave cell sizes (px, ALONG THE ACROSS-STROKE / Y axis of the
 * map — see `CRAYON_STREAK_STRETCH` for the along-stroke / X axis) per grain
 * level — the granularity of the FINEST, HIGHEST-AMPLITUDE octave in the
 * fractal stack (§2.5.1: "grain は支配的なスケールを選ぶ knob"). All 3
 * divide `CRAYON_THRESHOLD_SIZE` (256), and so does every coarser octave
 * derived from them (`<< o`, capped below `CRAYON_THRESHOLD_SIZE`), so every
 * level tiles seamlessly.
 */
export const CRAYON_GRAIN_DECAY_NUMS = [8, 10, 12];
/**
 * Denominator for `CRAYON_GRAIN_DECAY_NUMS` (= 8/16, 10/16, 12/16 → ½, ⅝, ¾).
 */
export const CRAYON_GRAIN_DECAY_DEN = 16;
/**
 * Cell size of the DOMINANT (finest, highest-amplitude) octave. **Fixed at 1
 * px for every grain level** (2026-08-21 実機 6 巡目, §2.5.3).
 *
 * 粗さを「支配オクターブのセルを大きくする」で表現していた版 (`[1, 2, 4]`) は、
 * **粗い側であからさまなモザイクになった** — grain がスタック全体を粗い側へ
 * ずらす作りだったため、粗レベルでは 1px 成分が完全に消え、4px の軸整列した
 * 正方形セルが剥き出しになっていた。
 *
 * 現行は**スタックの起点を常に 1px に固定**し、grain は
 * `CRAYON_GRAIN_DECAY_NUMS` (= 粗いオクターブの重み) だけを動かす。細かい成分が
 * どのレベルにも必ず残るので、粗くしても塊が大きくなるだけでモザイクにならない。
 */
const CRAYON_DOMINANT_CELL_SIZE = 1;
/**
 * How much wider a grain cell is along the map's X axis than its Y axis
 * (§2.5.2), before the `min(..., size)` clamp `buildCrayonThresholdMap`
 * applies. Under `mapping: 'world-rotated'` (below), the map's X axis
 * samples `lx` = the ROTATED coordinate ALONG the stroke direction
 * (`sampleTexel`/`sample_texel`'s `world-rotated` branch) and Y samples the
 * perpendicular `ly`. MUST be a power of two so every (pre-clamp) elongated
 * cell size (`cellSizeY * STRETCH`) stays a divisor of the 256×256 map (256
 * = 2^8's only divisors are powers of two) — the same seamless-tiling
 * constraint the octave cell sizes already have to satisfy.
 *
 * ## Why a small stretch is enough now (§2.5.2 4th real-device pass)
 *
 * The 3rd pass (`mapping: 'stamp'`) needed 32× to survive a "many
 * overlapping dabs, any one hit inks" collapse — see this file's module doc,
 * "v4" in the History section. `world-rotated` samples raw canvas
 * coordinates (no per-dab translation to the stamp centre), so a canvas
 * pixel maps to the SAME texel regardless of how many dabs of the current
 * segment's sweep hit it — the collapse this constant used to have to hide
 * cannot happen under this mapping. The stretch's only remaining job is
 * shaping the cell into a visibly elongated (streak-like) rectangle rather
 * than a square — a small single-digit multiplier is enough for that, and a
 * SMALL value is actually preferable: it keeps the along-length variation
 * (texel decisions still change every few multiples of the cell size as the
 * sweep progresses) that a 32× stretch had erased.
 */
const CRAYON_STREAK_STRETCH = 4;
/**
 * How many octaves stack on top of the grain-selected dominant cell size.
 * §2.5.1's design note suggested "4〜5" as a starting point; 6 is what an
 * actual render pass (`crayon-after.png`, see this package's Phase 11
 * implementation report) needed to read as organic clumps rather than fine
 * salt-and-pepper — §2.5.1 explicitly permits this kind of empirical
 * parameter tuning.
 */
const CRAYON_OCTAVE_COUNT = 5;
/**
 * Dominant (finest) octave's amplitude. Each successive (coarser, `<< o`)
 * octave gets `CRAYON_OCTAVE_DECAY` (¾) of the previous octave's amplitude
 * (`buildCrayonThresholdMap`'s loop), so the full 6-octave stack's
 * amplitudes are 110, 82, 61, 45, 33, 24.
 *
 * Note this does NOT keep the dominant term's amplitude above the SUM of
 * every coarser term (110 < 82+61+45+33+24 = 245) — an earlier revision of
 * this file tried to preserve that specific inequality (carried over,
 * un-examined, from §2.5's two-TERM design) and rendered as fine
 * salt-and-pepper (`crayon-preview.png` from the two-term revision), not
 * organic clumps. Re-deriving §2.5's actual lesson: the v1 bug wasn't
 * "coarse amplitude exceeded fine amplitude" in the abstract, it was that
 * the per-TEXEL (finest) variance was so small (±24) relative to a coarse
 * term spanning the FULL 0..255 range that individual texels inside one
 * coarse block had no room left to independently cross the threshold —
 * the whole block moved together. As long as the finest octave's amplitude
 * stays large IN ABSOLUTE TERMS (110, comparable to a typical
 * `densityLevel` gap), individual texels keep enough independent swing to
 * ink/skip differently from their neighbours no matter how large the
 * SUM of coarser terms grows — so growing the coarser octaves for more
 * visible large-scale clumping is safe (verified by rendering: no reversion
 * to solid mosaic blocks in `crayon-after.png`), even though the crude
 * "dominant > sum of others" inequality no longer holds numerically).
 */
const CRAYON_OCTAVE_BASE_AMP = 110;
const CRAYON_BASE_THRESHOLD = 128;
/** Default grain index (mid coarseness) when `penParams.grain` is omitted. */
export const CRAYON_DEFAULT_GRAIN = 0;
/**
 * Deterministic integer hash → pseudo-random in `[0, 2^32)`. Same
 * construction as `paperTexture.ts`'s `hash2` / `presetTextures.ts`'s
 * `hash3`, with its own salt constants so crayon's grain is independent of
 * paper's (2 different assets sampling the same `(x,y)` must not look
 * identical). Each octave uses a distinct salt (`CRAYON_OCTAVE_SALT_BASE +
 * octaveIndex`) so the octaves decorrelate from each other too — otherwise
 * every octave of the same cell-aligned `(gx,gy)` would repeat the same
 * pattern at different scales instead of contributing independent detail.
 */
const hash2 = (x, y, salt) => {
    let h = (Math.imul(x, 0x2545_f491) ^ Math.imul(y, 0x27d4_eb2d) ^ Math.imul(salt, 0x9e37_79b1)) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x85eb_ca6b) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0xc2b2_ae35) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h;
};
/** Salt base for the per-octave hash (§ hash2 doc). */
const CRAYON_OCTAVE_SALT_BASE = 211;
const clampThreshold = (v) => (v < 1 ? 1 : v > 255 ? 255 : v);
/**
 * Build one grain level's `size × size` fractal threshold map (§2.5.1,
 * anisotropic per §2.5.2). Stacks `CRAYON_OCTAVE_COUNT` octaves starting at
 * `baseCellSizeY` (the grain-selected, DOMINANT, finest/highest-amplitude
 * octave) and doubling the cell size (halving the amplitude) each further
 * octave. Each octave's cell is WIDER along X than Y (`cellSizeX =
 * min(cellSizeY * CRAYON_STREAK_STRETCH, size)`), so the grid cells are
 * elongated rectangles rather than squares — see `CRAYON_STREAK_STRETCH`'s
 * doc for why that reads as streaks once sampled via `mapping:
 * 'world-rotated'`. `cellSizeX` is CLAMPED to `size` (not dropped), which
 * makes an octave whose stretched X would otherwise exceed the map
 * X-INVARIANT once reached (a pure per-row band, contributing no
 * along-length variation within that octave) — `Math.floor(x / cellSizeX)`
 * is `0` for every in-range `x` once `cellSizeX >= size`, no special-casing
 * needed (unlike the abandoned `stamp`-mapping v4, whose per-dab
 * recentring forced a half-cell phase-offset workaround this version does
 * NOT need — see the removed workaround's history in git blame /
 * `phase11-light-pens-2026-08-21.md` §2.5.2 "② bug" for why `world-rotated`
 * doesn't have that failure mode: there is no single canvas point every dab
 * shares, so no boundary is privileged the way the stamp centre was). Each
 * octave independently hashes its own `(gx, gy)` grid cell (own salt, own
 * cell size) and contributes a signed offset in `±amplitude` around the
 * shared base threshold; the sum of all octaves is clamped into `[1, 255]`
 * (`clampThreshold`, mirrors `toneThreshold.ts`'s "never 0" rule).
 */
const buildCrayonThresholdMap = (size, decayNum) => {
    const cellSizesX = [];
    const cellSizesY = [];
    const amps = [];
    let amp = CRAYON_OCTAVE_BASE_AMP;
    for (let o = 0; o < CRAYON_OCTAVE_COUNT; o++) {
        const cellSizeY = CRAYON_DOMINANT_CELL_SIZE << o;
        if (cellSizeY > size)
            break; // defensive; not expected for 1 << 4 = 16, size=256
        const cellSizeX = Math.min(cellSizeY * CRAYON_STREAK_STRETCH, size);
        cellSizesX.push(cellSizeX);
        cellSizesY.push(cellSizeY);
        amps.push(amp);
        // grain (= 粗さ) は**粗いオクターブの重み**で表現する (§2.5.3)。減衰が緩い
        // ほど粗いオクターブが効いて塊が大きくなるが、支配オクターブは常に 1px
        // なので細かい成分が消えず、モザイクにならない。
        amp = Math.floor((amp * decayNum) / CRAYON_GRAIN_DECAY_DEN);
    }
    const out = new Uint8Array(size * size);
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            let level = CRAYON_BASE_THRESHOLD;
            for (let o = 0; o < cellSizesX.length; o++) {
                const cellSizeX = cellSizesX[o];
                const cellSizeY = cellSizesY[o];
                const a = amps[o];
                const gx = Math.floor(x / cellSizeX);
                const gy = Math.floor(y / cellSizeY);
                const n = (hash2(gx, gy, CRAYON_OCTAVE_SALT_BASE + o) % (a * 2 + 1)) - a;
                level += n;
            }
            out[y * size + x] = clampThreshold(level);
        }
    }
    return out;
};
/**
 * One pre-baked `TextureSampler` per grain level, built once at module load.
 * `mapping: 'world-rotated'` (§2.5.2 4th pass, was `'stamp'` in the 3rd
 * pass, `'world'` before that) — the texel grid rotates with the stroke
 * direction (`textureDir`) like `stamp` does, but samples raw canvas
 * coordinates instead of translating to the stamp centre (module doc for
 * why). `scale: 1` keeps 1 canvas px per texel, same as before; the 256×256
 * map's period comfortably exceeds `MAX_STROKE_WIDTH` (64).
 */
export const CRAYON_THRESHOLD_SAMPLERS = CRAYON_GRAIN_DECAY_NUMS.map((decayNum) => ({
    mapping: 'world-rotated',
    width: CRAYON_THRESHOLD_SIZE,
    height: CRAYON_THRESHOLD_SIZE,
    alpha: buildCrayonThresholdMap(CRAYON_THRESHOLD_SIZE, decayNum),
    scale: 1,
}));
/**
 * Clamp/quantise a wire-level `grain` (from `penParams.crayon.grain`,
 * optional) into a valid index of `CRAYON_THRESHOLD_SAMPLERS` /
 * `CRAYON_GRAIN_DECAY_NUMS`. `undefined` → `CRAYON_DEFAULT_GRAIN`. Out-of-
 * range values clamp (defensive — the protocol schema already rejects them
 * at intake, this is the render-side belt).
 */
export const crayonGrainIndex = (grain) => {
    const g = Math.trunc(grain ?? CRAYON_DEFAULT_GRAIN);
    const max = CRAYON_GRAIN_DECAY_NUMS.length - 1;
    return g < 0 ? 0 : g > max ? max : g;
};
//# sourceMappingURL=crayonThreshold.js.map